import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { usePublicConfig } from '../../hooks/usePublicConfig'
import { useActiveBanners, useDismissBanner } from '../../hooks/useBanners'
import { createNadDebug } from '../../utils/debugLog'
import type { ActiveBanner } from '../../api/banners'
import type { BannerSeverity } from './bannerSeverity'
import { isDismissed, persistDismissed } from './dismissalStore'
import { resolveBannerText } from './resolveBannerText'
import { normalizeLocale } from '../../i18n/supportedLocales'
import { useBannerHeight } from './useBannerHeight'
import StickyBannerStrip from '../layout/StickyBannerStrip'
import BannerPopupModal from '../layout/BannerPopupModal'

const log = createNadDebug('dw:dynamic-banner:visible')
const MAINTENANCE_DISMISS_PREFIX = 'maintenance-banner-dismissed:'

const SEVERITY_RANK: Record<BannerSeverity, number> = {
  critical: 3,
  warning: 2,
  info: 1,
}

function readMaintenanceDismissed(version: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(`${MAINTENANCE_DISMISS_PREFIX}${version}`) === '1'
  } catch {
    return false
  }
}

function persistMaintenanceDismissed(version: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(`${MAINTENANCE_DISMISS_PREFIX}${version}`, '1')
  } catch {
    // storage blocked — in-memory hide still applies for this pageview.
  }
}

function bySeverityThenPosted(a: ActiveBanner, b: ActiveBanner): number {
  // Null-safe: fall back to 0-rank / empty string so a malformed banner (missing
  // severity/posted_at) can never throw in the sort — this component has no
  // error boundary above the BannerBoundary wrapper and must stay crash-proof.
  const rank = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0)
  if (rank !== 0) return rank
  return (a.posted_at ?? '').localeCompare(b.posted_at ?? '')
}

/**
 * Single reactive owner of ALL top-of-page banner UI (sticky strip + popups)
 * and of the `--banner-height` CSS variable. Replaces the standalone
 * <MaintenanceBanner /> mount.
 *
 * Precedence: the env-var maintenance banner (outage-proof, from usePublicConfig
 * which has a build-time env fallback) always wins the single sticky slot. While
 * it is visible, no dynamic sticky banner renders — so exactly one component ever
 * measures/writes `--banner-height`. Popups render regardless (below the strip).
 *
 * CONSENT: this only mounts inside <App/>, which ConsentProvider does not render
 * until the user acknowledges the cookie modal — so no /banners/active fetch or
 * storage write happens before consent. Never mount this above ConsentProvider
 * or inside ConsentBackdrop.
 */
export default function BannerHost() {
  const { t, i18n } = useTranslation('workspace')
  // Active UI locale drives which stored override renders. `useTranslation`
  // subscribes to i18next's `languageChanged`, so a switch re-renders this
  // component and the banner text swaps with no refetch — the whole point of
  // resolving client-side.
  const locale = normalizeLocale(i18n.language) ?? 'en'
  const { user } = useAuth()
  const config = usePublicConfig()
  const banners = useActiveBanners()
  const dismissMutation = useDismissBanner()

  // In-memory dismissals for this pageview (covers recurrent + instant hide of
  // one-time before the query refetches). Reactive so dismiss re-renders.
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set())

  // Maintenance banner (env-driven) dismissal — reactive state in this owner.
  // `usePublicConfig()` is undefined on the first render(s), so we must NOT seed
  // dismissal from a one-time initializer (the version isn't known yet). Instead
  // derive it every render: this-session dismissals live in `dismissedVersions`,
  // and prior-session dismissals are read from sessionStorage once the version
  // resolves — so a previously dismissed maintenance banner stays hidden.
  const maintenance = config?.maintenanceBanner
  const maintenanceVersion = maintenance?.version ?? ''
  const [dismissedVersions, setDismissedVersions] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const maintenanceDismissed =
    !!maintenanceVersion &&
    (dismissedVersions.has(maintenanceVersion) ||
      readMaintenanceDismissed(maintenanceVersion))
  const maintenanceVisible = !!maintenance?.enabled && !maintenanceDismissed

  const stripRef = useRef<HTMLDivElement>(null)

  const isBannerHidden = useCallback(
    (b: ActiveBanner): boolean => {
      if (dismissedIds.has(b.id)) return true
      // One-time: also honour persisted (revision-aware) localStorage dismissal.
      if (b.dismiss_mode === 'one_time' && isDismissed(b.id, b.revision)) return true
      return false
    },
    [dismissedIds],
  )

  const visibleBanners = useMemo(
    () => banners.filter((b) => !isBannerHidden(b)),
    [banners, isBannerHidden],
  )

  const stickyBanner = useMemo(() => {
    const sticky = visibleBanners
      .filter((b) => b.display_mode === 'banner')
      .sort(bySeverityThenPosted)
    return sticky[0]
  }, [visibleBanners])

  const activePopup = useMemo(() => {
    const popups = visibleBanners
      .filter((b) => b.display_mode === 'popup')
      .sort(bySeverityThenPosted)
    return popups[0]
  }, [visibleBanners])

  // Resolve display copy for the active locale. Done here rather than inside the
  // presentational strips so both they and the NAD signal below see one result.
  const stickyText = useMemo(
    () =>
      stickyBanner
        ? {
            title: resolveBannerText(stickyBanner.title, stickyBanner.title_i18n, locale),
            message: resolveBannerText(stickyBanner.message, stickyBanner.message_i18n, locale),
          }
        : null,
    [stickyBanner, locale],
  )

  const popupText = useMemo(
    () =>
      activePopup
        ? {
            title: resolveBannerText(activePopup.title, activePopup.title_i18n, locale),
            message: resolveBannerText(activePopup.message, activePopup.message_i18n, locale),
          }
        : null,
    [activePopup, locale],
  )

  // BannerHost is the SOLE owner of --banner-height. Measure whichever sticky
  // strip is actually rendered (maintenance takes precedence over dynamic).
  // The slotKey changes when the rendered strip swaps so the ResizeObserver
  // re-attaches to the new node instead of the detached previous one.
  //
  // NOTE: slotKey deliberately carries no locale, so useBannerHeight's layout
  // effect does NOT re-run on a language switch. The height still republishes
  // because the ResizeObserver stays attached to the same (unchanged) DOM node
  // and fires when the translated text reflows to a different line count. That
  // is load-bearing: if this hook is ever refactored to effect-driven measuring,
  // add the locale to slotKey or ko/vi will silently reintroduce the RFC-1081
  // navbar-overlap bug while English stays correct.
  const stickyActive = maintenanceVisible || !!stickyBanner
  const slotKey = maintenanceVisible
    ? `maintenance:${maintenanceVersion}`
    : stickyBanner
      ? `dynamic:${stickyBanner.id}:${stickyBanner.revision}`
      : 'none'
  useBannerHeight(stripRef, stickyActive, slotKey)

  const dismissDynamic = useCallback(
    (b: ActiveBanner, permanent: boolean) => {
      setDismissedIds((prev) => {
        const next = new Set(prev)
        next.add(b.id)
        return next
      })
      if (b.dismiss_mode === 'one_time' && permanent) {
        persistDismissed(b.id, b.revision)
        // Authenticated → persist server-side (cross-device). Anonymous → localStorage only.
        if (user?.id) {
          dismissMutation.mutate(b.id)
        }
      }
      log({
        event: 'dynamic-banner-dismiss',
        trace_id: 'dynamic-banner',
        feature: 'dynamic-banner',
        banner_id: b.id,
        display_mode: b.display_mode,
        dismiss_mode: b.dismiss_mode,
        permanent,
        authenticated: !!user?.id,
      })
    },
    [dismissMutation, user?.id],
  )

  const dismissMaintenance = useCallback(() => {
    if (!maintenance) return
    persistMaintenanceDismissed(maintenance.version)
    setDismissedVersions((prev) => {
      const next = new Set(prev)
      next.add(maintenance.version)
      return next
    })
  }, [maintenance])

  log({
    event: 'banner-host-render',
    trace_id: 'dynamic-banner',
    feature: 'dynamic-banner',
    maintenance_visible: maintenanceVisible,
    sticky_banner_id: stickyBanner?.id ?? null,
    popup_banner_id: activePopup?.id ?? null,
    total_active: banners.length,
    // Locale + fallback flags answer the one question a screenshot cannot: did
    // this viewer actually get their language, or silently get English? A
    // fallback renders as perfectly valid copy and is otherwise invisible.
    locale,
    sticky_title_fell_back: stickyText?.title.fellBack ?? null,
    sticky_message_fell_back: stickyText?.message.fellBack ?? null,
    popup_title_fell_back: popupText?.title.fellBack ?? null,
    popup_message_fell_back: popupText?.message.fellBack ?? null,
  })

  return (
    <>
      {maintenanceVisible && maintenance ? (
        <StickyBannerStrip
          severity={maintenance.severity}
          title={t('maintenanceBanner.headsUp')}
          message={maintenance.message}
          dismissible={maintenance.dismissible}
          onDismiss={dismissMaintenance}
          dismissLabel={t('maintenanceBanner.dismissBanner')}
          innerRef={stripRef}
          testId="maintenance-banner"
          dataAttrs={{ 'data-banner-version': maintenance.version }}
        />
      ) : stickyBanner && stickyText ? (
        <StickyBannerStrip
          severity={stickyBanner.severity}
          title={stickyText.title.text}
          message={stickyText.message.text}
          renderMarkdown
          dismissible
          onDismiss={() => dismissDynamic(stickyBanner, true)}
          dismissLabel={t('banners.dismiss')}
          innerRef={stripRef}
          testId="dynamic-banner"
          dataAttrs={{ 'data-banner-id': stickyBanner.id }}
        />
      ) : null}

      {activePopup && popupText && (
        <BannerPopupModal
          key={activePopup.id}
          title={popupText.title.text}
          message={popupText.message.text}
          severity={activePopup.severity}
          oneTime={activePopup.dismiss_mode === 'one_time'}
          onClose={(permanently) => dismissDynamic(activePopup, permanently)}
        />
      )}
    </>
  )
}
